// Cliente HTTP de la configuración del asistente IA (helpdesk/ai-settings).
//
// Calca el patrón de reports/reportsApi.js: mismo BFF, mismos headers Bearer,
// mismos errores tipados con `.status` para poder distinguir "backend aún no
// desplegado" (isEndpointMissing) de un error real.
//
// SEGURIDAD: el token del proveedor viaja SOLO en el body de PUT/POST (nunca
// en query/URL) y el backend jamás lo devuelve completo — el GET entrega
// únicamente has_token/token_last4. Prohibido loguear payloads acá.

// throwFetchApiError replaces the throwApiError that used to be copied in every
// helpdesk client: it keeps status/code/capabilities and also exposes the raw
// ValidationPipe list in err.messages.
import { throwFetchApiError } from "../../../../utils/apiErrorMessage";
import { authFetch } from "../../../../services/authFetch";

// REACT_APP_API_CONNECTION ya trae /api/v1/ al final; se recorta para armar
// rutas /api/v1/... sin duplicar el prefijo.
const BFF_BASE_URL = (process.env.REACT_APP_API_CONNECTION || "").replace(/\/api\/v1\/?$/, "");

function buildAuthHeaders(extra = {}) {
  // El Authorization lo pone authFetch (access vigente + un reintento ante 401).
  return {
    "x-app": "web",
    application: "web",
    ...extra,
  };
}

/**
 * true cuando el error viene de una ruta aún no desplegada (404/405 del BFF).
 * @param {unknown} err
 * @returns {boolean}
 */
export const isEndpointMissing = (err) => [404, 405].includes(err?.status);

/**
 * Configuración vigente del asistente para el tenant.
 * Contrato: { ok, settings: { mode:'off'|'byo', provider, model,
 * system_prompt, has_token, token_last4 } } — nunca el token completo.
 * @returns {Promise<object>}
 */
export async function getAiSettings() {
  const response = await authFetch(`${BFF_BASE_URL}/api/v1/helpdesk/ai-settings`, {
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Canales y cuentas (números/páginas) del tenant con el estado EFECTIVO del
 * asistente en cada uno. Contrato: { ok, channels: [{ channel, label, enabled,
 * accounts: [{ key, label, phone, status, enabled, overridden }] }] }.
 *
 * La `key` de cada cuenta la arma el BACKEND y el front la devuelve tal cual
 * en el PUT — nunca la construye a mano: si divergiera, se apagaría una clave
 * que el gate de mensajes jamás consulta (perilla muda).
 * @returns {Promise<object>}
 */
export async function getAiChannelScope() {
  const response = await authFetch(`${BFF_BASE_URL}/api/v1/helpdesk/ai-settings/channels`, {
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Catálogo de proveedores/modelos que sirve el backend (fuente de verdad
 * cuando existe). Contrato: { ok, providers: [{ id, label, keyUrl,
 * models: [{ id, label, badge, pros, cons }] }] }. Ante 404/405
 * (isEndpointMissing) el caller usa la copia local de aiModelCatalog.js.
 * @returns {Promise<object>}
 */
export async function getAiModelCatalog() {
  const response = await authFetch(`${BFF_BASE_URL}/api/v1/helpdesk/ai-settings/catalog`, {
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Grupos de WhatsApp del tenant con actividad reciente (más reciente primero),
 * para elegir el grupo del equipo de los avisos de derivación. Contrato:
 * { groups: [{ jid, name, last_message_at }] }; `name` puede ser el jid.
 * Vive en microservice-conversations; ante 404/405 (isEndpointMissing) la
 * tarjeta sigue funcionando sin la lista.
 * @returns {Promise<object>}
 */
export async function getWhatsappGroups() {
  const response = await authFetch(`${BFF_BASE_URL}/api/v1/conversations/whatsapp-groups`, {
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Guarda la configuración. `payload.api_token` va SOLO si el usuario tipeó un
 * token nuevo: un PUT parcial sin esa clave no pisa el token ya guardado.
 * @param {object} payload
 * @returns {Promise<object>} settings enmascarados (has_token/token_last4)
 */
export async function saveAiSettings(payload) {
  const response = await authFetch(`${BFF_BASE_URL}/api/v1/helpdesk/ai-settings`, {
    method: "PUT",
    headers: buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Prueba de conexión real contra el proveedor (user-triggered, timeout en el
 * backend). Manda api_token solo si el usuario tipeó uno; con {} el backend
 * usa el token guardado.
 * @param {object} [payload]
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export async function testAiSettings(payload = {}) {
  const response = await authFetch(`${BFF_BASE_URL}/api/v1/helpdesk/ai-settings/test`, {
    method: "POST",
    headers: buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}
